'use client';

import { useState, useMemo } from 'react';

interface JournalEntry {
  id: number;
  description: string;
  date: string;
  status: string;
}

interface JournalTableProps {
  entries: JournalEntry[];
  onEdit?: (entry: JournalEntry) => void;
  onDelete?: (id: number) => void;
  isLoading?: boolean;
}

export const JournalTable: React.FC<JournalTableProps> = ({
  entries,
  onEdit,
  onDelete,
  isLoading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredEntries = useMemo(() => {
    if (!Array.isArray(entries)) return [];
    return entries.filter((entry) =>
      entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.date.includes(searchTerm)
    );
  }, [entries, searchTerm]);

  if (isLoading) {
    return <div className="text-center py-8">Carregant assentaments...</div>;
  }

  if (!Array.isArray(entries) || entries.length === 0) {
    return <div className="text-center py-8 text-gray-500">No hi ha assentaments registrats</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <input
          type="text"
          placeholder="Cercar assentaments..."
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border rounded"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-2 text-left">ID</th>
              <th className="border p-2 text-left">Descripció</th>
              <th className="border p-2 text-left">Data</th>
              <th className="border p-2 text-left">Estat</th>
              <th className="border p-2 text-center">Accions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEntries.map((entry) => (
              <tr key={entry.id} className="hover:bg-gray-50">
                <td className="border p-2">{entry.id}</td>
                <td className="border p-2">{entry.description}</td>
                <td className="border p-2">{entry.date}</td>
                <td className="border p-2">{entry.status}</td>
                <td className="border p-2 text-center">
                  {onEdit && (
                    <button
                      onClick={() => onEdit(entry)}
                      className="px-2 py-1 bg-gray-600 text-white text-sm rounded mr-2"
                    >
                      Editar
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={() => onDelete(entry.id)}
                      className="px-2 py-1 bg-red-500 text-white text-sm rounded"
                    >
                      Eliminar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
